/**
 * JobCommandCenter — employer hiring command center for a single owned native job.
 * Read-only aggregation of canonical jobs/job_applications/invites via
 * GET /api/jobs/:jobId/hiring-summary. No new counters/model. Renders a clickable funnel,
 * Action Needed (oldest waiting first), recent activity, outreach, and job-specific navigation.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Users, Clock, MessageSquareWarning, CalendarCheck, BadgeCheck, Trophy, Send,
  Search, FileText, Loader2, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function waitAge(iso) {
  if (!iso) return 'New';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'New' : `${d} day${d === 1 ? '' : 's'} waiting`;
}
function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return ''; }
}

export function JobCommandCenter({ jobId, jobTitle }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/jobs/${jobId}/hiring-summary`, { withCredentials: true })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [jobId]);

  const toApplications = (stage) =>
    navigate(`/employer/dashboard?tab=applications&job=${jobId}${stage ? `&stage=${stage}` : ''}`);
  const toFindTalent = () => navigate(`/candidates/search?rankJob=${jobId}`);

  if (loading) {
    return <div className="flex justify-center py-10" data-testid="command-center-loading"><Loader2 className="w-5 h-5 animate-spin text-brand" /></div>;
  }
  if (!data) return null;

  const f = data.funnel || {};
  // Only counts backed by canonical data. Views intentionally omitted (not reliably tracked).
  // Each card is a true job-scoped drill-down into the Applications experience (stage-filtered).
  const metrics = [
    { key: 'applicants', label: 'Applicants', value: f.applicants, icon: Users, onClick: () => toApplications() },
    { key: 'needs_response', label: 'Needs Response', value: f.needs_response, icon: MessageSquareWarning, accent: f.needs_response > 0, onClick: () => toApplications('needs_response') },
    { key: 'interviewing', label: 'Interviewing', value: f.interviewing, icon: CalendarCheck, onClick: () => toApplications('interviewing') },
    { key: 'offers', label: 'Offers', value: f.offers, icon: BadgeCheck, onClick: () => toApplications('offers') },
    { key: 'hired', label: 'Hired', value: f.hired, icon: Trophy, onClick: () => toApplications('hired') },
    { key: 'invited', label: 'Invited', value: data.invited, icon: Send, onClick: toFindTalent },
  ];

  return (
    <div className="space-y-6" data-testid="job-command-center">
      {/* Hiring funnel */}
      <Card className="p-5 rounded-2xl border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-slate-900">Hiring snapshot</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-slate-200 h-8" onClick={toFindTalent} data-testid="cc-find-talent-btn">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Find Talent for this Role
            </Button>
            <Button size="sm" className="bg-brand hover:bg-brand-hover text-white h-8" onClick={() => toApplications()} data-testid="cc-view-applicants-btn">
              View All Applicants
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {metrics.map(m => (
            <button
              key={m.key}
              type="button"
              onClick={m.onClick}
              data-testid={`cc-metric-${m.key}`}
              className={`text-left rounded-xl border p-3 transition-colors hover:border-brand/40 hover:bg-brand-light/40 ${m.accent ? 'border-amber-200 bg-amber-50' : 'border-slate-200'}`}
            >
              <m.icon className={`w-4 h-4 mb-2 ${m.accent ? 'text-amber-600' : 'text-slate-400'}`} />
              <div className={`text-2xl font-bold ${m.accent ? 'text-amber-700' : 'text-slate-900'}`}>{m.value ?? 0}</div>
              <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{m.label}</div>
            </button>
          ))}
        </div>
        {data.invited > 0 && (
          <p className="mt-3 text-xs text-slate-500" data-testid="cc-outreach-line">
            {data.invited_applied} of {data.invited} invited candidate{data.invited === 1 ? '' : 's'} later applied.
          </p>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Action needed */}
        <Card className="p-5 rounded-2xl border border-slate-200" data-testid="cc-action-needed">
          <h2 className="text-base font-bold text-slate-900 mb-3">Action needed</h2>
          {data.action_needed?.length ? (
            <div className="space-y-2">
              {data.action_needed.map(a => (
                <button
                  key={a.application_id}
                  type="button"
                  onClick={() => navigate(`/applications/${a.application_id}?origin=job&job=${jobId}`)}
                  data-testid={`cc-action-${a.application_id}`}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{a.applicant_name}</p>
                    <p className="text-xs text-slate-500">{a.stage_label} · <span className="text-amber-600 font-medium">{waitAge(a.waiting_since)}</span></p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-brand flex-shrink-0">Open <ChevronRight className="w-3.5 h-3.5" /></span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-4">Nothing waiting on you right now.</p>
          )}
        </Card>

        {/* Recent activity */}
        <Card className="p-5 rounded-2xl border border-slate-200" data-testid="cc-activity">
          <h2 className="text-base font-bold text-slate-900 mb-3">Recent activity</h2>
          {data.recent_activity?.length ? (
            <ol className="space-y-2.5">
              {data.recent_activity.map((e, i) => (
                <li key={i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-700 truncate"><span className="font-medium text-slate-900">{e.applicant_name}</span> — {e.label}</span>
                  <span className="text-xs text-slate-400 flex-shrink-0">{fmt(e.at)}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-slate-500 py-4">No activity yet for this role.</p>
          )}
        </Card>
      </div>
    </div>
  );
}

export default JobCommandCenter;
